import React, { useState } from 'react';
import { Linking, Pressable, StyleSheet, Text, View } from 'react-native';
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react-native';
import { apiService } from '../../services/apiService';
import { writeBillingSnapshot } from '../../utils/billingSnapshot';
import { trackBillingAction } from '../../utils/billingAnalytics';

export default function BillingModalRecentCharges({ summary, source }) {
  const posthog = usePostHog();
  const [expanded, setExpanded] = useState(false);
  const [pending, setPending] = useState(false);

  if (!summary) return null;
  const charges = summary.recentCharges ?? [];

  async function handleSeeAll() {
    if (pending) return;
    setPending(true);
    trackBillingAction(posthog, 'billing_action_clicked', { action: 'full_portal', status: summary.status, source });

    try {
      writeBillingSnapshot(summary);
      const idempotencyKey = crypto.randomUUID();
      const body = await apiService.invokeEdgeFunction('billing-action', {
        body: { action: 'full_portal' },
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      if (body.portalUrl) {
        trackBillingAction(posthog, 'billing_action_completed', { action: 'full_portal', status: summary.status, source });
        try { window.open(body.portalUrl, '_blank', 'noopener,noreferrer'); }
        catch { Linking.openURL(body.portalUrl); }
      }
    } catch (e) {
      trackBillingAction(posthog, 'billing_action_failed', { action: 'full_portal', status: summary.status, source, error_code: e?.code ?? e?.message });
      Sentry.captureException(e, {
        tags: {
          component: 'BillingModalRecentCharges', action: 'full_portal',
          status: String(e?.status ?? 'n/a'), error_code: e?.code ?? 'n/a',
        },
        contexts: { billing: { source, summary_status: summary?.status } },
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <View style={styles.root}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        aria-expanded={expanded}
        onPress={() => setExpanded((v) => !v)}
        style={({ pressed }) => [styles.toggle, pressed && { opacity: 0.7 }]}
      >
        <Text style={styles.toggleLabel}>Recent charges</Text>
        <Text style={styles.toggleArrow}>{expanded ? '▴' : '▾'}</Text>
      </Pressable>

      {expanded && (
        <View style={styles.list}>
          {charges.length === 0 ? (
            <Text style={styles.empty}>No charges yet</Text>
          ) : (
            charges.map((c) => (
              <View key={c.transactionId} style={styles.chargeRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.chargeAmount}>{c.amount?.amountFormatted ?? ''}</Text>
                  <Text style={styles.chargeMeta}>{formatDate(c.billedAt)} · {c.status}</Text>
                </View>
                {c.invoicePdfUrl && (
                  <Pressable onPress={() => Linking.openURL(c.invoicePdfUrl)}>
                    <Text style={styles.pdfLink}>PDF</Text>
                  </Pressable>
                )}
              </View>
            ))
          )}
          <Pressable
            accessibilityRole="link"
            onPress={handleSeeAll}
            disabled={pending}
            style={({ pressed }) => [styles.seeAll, pressed && { opacity: 0.6 }]}
          >
            <Text style={styles.seeAllText}>{pending ? 'Opening...' : 'See all billing history'}</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

const styles = StyleSheet.create({
  root: { paddingHorizontal: 20, paddingVertical: 12, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)' },
  toggle: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6 },
  toggleLabel: { fontSize: 13, fontWeight: '600', color: '#FFFFFF' },
  toggleArrow: { fontSize: 14, color: 'rgba(255,255,255,0.6)' },
  list: { marginTop: 10, gap: 8 },
  empty: { fontSize: 13, color: 'rgba(255,255,255,0.5)' },
  chargeRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },
  chargeAmount: { fontSize: 14, fontWeight: '600', color: '#FFFFFF' },
  chargeMeta: { fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 },
  pdfLink: { fontSize: 13, color: '#6FA0FF', fontWeight: '600' },
  seeAll: { marginTop: 6 },
  seeAllText: { fontSize: 13, fontWeight: '600', color: '#6FA0FF' },
});
